import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CorporateCreateComponent from "./component";

// Import Modal components
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const CorporateCreateContainer = (props) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fetchingDepartments, setFetchingDepartments] = useState(false);
  
  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [corporateId, setCorporateId] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);

  // Corporate data
  const [createCorporateData, setCreateCorporateData] = useState({
    name: "",
    domains: []
  });

  // Department data
  const [useDefaultDepartment, setUseDefaultDepartment] = useState(true);
  const [defaultBalance, setDefaultBalance] = useState("");
  const [departments, setDepartments] = useState([
    { departmentId: "", initialBalance: "", name: "" }
  ]);
  const [availableDepartments, setAvailableDepartments] = useState([]);

  // Usage limit data - NEW STRUCTURE
  const [usageLimitRequired, setUsageLimitRequired] = useState("No");
  const [usageLimits, setUsageLimits] = useState([]);

  // Plan data
  const [planData, setPlanData] = useState({
    monthly: {
      name: "Monthly Plan",
      amount: 5000,
      resetBalance: true
    }
  });

  const navigate = useNavigate();

  const handleNext = () => {
    // Validate current step before moving forward
    if (activeStep === 0) {
      // Validate corporate details
      if (!createCorporateData.name.trim()) {
        toast.error("Please enter business name");
        return;
      }

      // Check if there's at least one domain with non-empty value
      const hasValidDomains = createCorporateData.domains && 
        createCorporateData.domains.some(domain => domain.domain && domain.domain.trim());
      
      if (!hasValidDomains) {
        toast.error("Please enter at least one domain");
        return;
      }
      
      setActiveStep((prevStep) => prevStep + 1);
      
    } else if (activeStep === 1) {
      // Validate departments based on checkbox state
      if (useDefaultDepartment) {
        // Validate default balance
        if (!defaultBalance || parseFloat(defaultBalance) <= 0) {
          toast.error("Please enter a valid default balance");
          return;
        }
      } else {
        // Validate custom departments
        for (let dept of departments) {
          if (!dept.name?.trim()) {
            toast.error("Please select department name for all departments");
            return;
          }
          if (!dept.departmentId || dept.departmentId === "") {
            toast.error("Please select a valid department from dropdown");
            return;
          }
          if (!dept.initialBalance || parseFloat(dept.initialBalance) <= 0) {
            toast.error("Please enter a valid initial balance for all departments");
            return;
          }
        }
      }
      
      setActiveStep((prevStep) => prevStep + 1);
      
    } else if (activeStep === 2) {
      // Validate usage limit if required
      if (usageLimitRequired === "Yes" && usageLimits.length === 0) {
        toast.error("Please add at least one usage limit");
        return;
      }
      
      // Final validation and submit
      handleFinalSubmit();
    }
  };

  const handleClose = () => {
    if (activeStep === 0) {
      navigate(-1);
    } else {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  // Handle corporate data changes
  const handleChangeCorporate = (e) => {
    const { name, value } = e.target;
    
    if (name === "domain") {
      // Check if domain already exists
      const domainExists = createCorporateData.domains.some(
        domain => domain.domain.toLowerCase() === value.toLowerCase()
      );
      
      if (!domainExists && value.trim()) {
        // Add new domain to the array
        setCreateCorporateData({
          ...createCorporateData,
          domains: [
            ...createCorporateData.domains,
            { domain: value.trim() }
          ]
        });
      } else if (domainExists) {
        toast.warning("Domain already added");
      }
    } else {
      setCreateCorporateData({
        ...createCorporateData,
        [name]: value,
      });
    }
  };

  // Fetch available departments from API
  useEffect(() => {
    handleGetDepartmentsList();
  }, []);

  const handleGetDepartmentsList = () => {
    setFetchingDepartments(true);
    props.getDataFromAPI(
      `/partner/api/v2/corporate/all/departments`,
      "GET",
      undefined,
      (response) => {
        setFetchingDepartments(false);
        console.log("Departments API response:", response);
        if (response && Array.isArray(response)) {
          const formattedDepartments = response.map(dept => ({
            id: dept.departmentId || dept.id || dept.department_id,
            name: dept.departmentName || dept.name || dept.department_name
          })).filter(dept => dept.id && dept.name);
          
          setAvailableDepartments(formattedDepartments);
          console.log("Formatted departments:", formattedDepartments);
        } else {
          toast.warning("No departments available from API");
          setAvailableDepartments([]);
        }
      },
      (err) => {
        setFetchingDepartments(false);
        console.error("Failed to fetch departments:", err);
        toast.warning("Using default departments");
        setAvailableDepartments([]);
      }
    );
  };

  // Handle checkbox change
  const handleUseDefaultChange = (e) => {
    const isChecked = e.target.checked;
    setUseDefaultDepartment(isChecked);
    
    // Reset to default state when toggling
    if (isChecked) {
      // When checking the box, reset departments
      setDefaultBalance("");
      setDepartments([{ departmentId: "", initialBalance: "", name: "" }]);
    }
  };

  // Handle default balance change
  const handleDefaultBalanceChange = (value) => {
    const regex = /^\d*\.?\d*$/;
    if (regex.test(value) || value === "") {
      setDefaultBalance(value);
    }
  };

  // Add new department (only when checkbox is unchecked)
  const handleAddDepartment = () => {
    // Add a new empty department
    setDepartments([
      ...departments,
      { departmentId: "", initialBalance: "", name: "" }
    ]);
  };

  const handleRemoveDepartment = (index) => {
    if (departments.length > 1) {
      const newDepartments = [...departments];
      newDepartments.splice(index, 1);
      setDepartments(newDepartments);
    }
  };

  const handleDepartmentChange = (index, field, value) => {
    const newDepartments = [...departments];
    
    if (field === 'name') {
      const selectedDept = availableDepartments.find(dept => dept.name === value);
      if (selectedDept) {
        newDepartments[index].departmentId = selectedDept.id;
        newDepartments[index].name = selectedDept.name;
      } else {
        newDepartments[index].departmentId = "";
        newDepartments[index].name = value;
      }
    } else {
      newDepartments[index][field] = field === 'initialBalance' 
        ? (parseInt(value) || 0) 
        : value;
    }
    
    setDepartments(newDepartments);
  };

  const handleBalanceChange = (index, value) => {
    const regex = /^\d*\.?\d*$/;
    if (regex.test(value) || value === "") {
      const newDepartments = [...departments];
      newDepartments[index].initialBalance = value;
      setDepartments(newDepartments);
    }
  };

  // Handle usage limit changes - UPDATED
  const handleUsageLimitChange = (value) => {
    setUsageLimitRequired(value);
    if (value === "No") {
      setUsageLimits([]);
    }
  };

  // Handle usage limits change from component
  const handleUsageLimitsChange = (limits) => {
    setUsageLimits(limits);
  };

  // Handle plan changes
  const handlePlanChange = (field, value) => {
    setPlanData(prev => ({
      ...prev,
      monthly: {
        ...prev.monthly,
        [field]: field === 'amount' ? parseInt(value) || 0 : value
      }
    }));
  };

  const handleResetBalanceChange = (value) => {
    setPlanData(prev => ({
      ...prev,
      monthly: {
        ...prev.monthly,
        resetBalance: value
      }
    }));
  };

  // Copy corporate ID to clipboard
  const handleCopyCorporateId = () => {
    if (corporateId) {
      navigator.clipboard.writeText(corporateId)
        .then(() => {
          setCopySuccess(true);
          toast.success("Corporate ID copied to clipboard!");
          
          // Reset copy success after 3 seconds
          setTimeout(() => {
            setCopySuccess(false);
          }, 3000);
        })
        .catch(err => {
          console.error('Failed to copy: ', err);
          toast.error("Failed to copy Corporate ID");
        });
    }
  };

  // Handle success modal close
  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate(`/home/customermanager/corporatedashboard/${corporateId}`);
  };

  // Handle final submission - UPDATED
  const handleFinalSubmit = () => {
    setLoading(true);
    
    const mainPayload = {
      name: createCorporateData.name.trim(),
      domains: createCorporateData.domains
        .filter(domain => domain.domain && domain.domain.trim())
        .map(domain => ({
          domain: domain.domain.trim()
        })),
      useDefaultDepartment: useDefaultDepartment,
      plan: {
        monthly: {
          name: planData.monthly.name,
          amount: parseInt(planData.monthly.amount) || 5000,
          resetBalance: planData.monthly.resetBalance
        }
      }
    };

    // Add department data based on checkbox state
    if (useDefaultDepartment) {
      mainPayload.defaultBalance = parseInt(defaultBalance) || 0;
      mainPayload.departments = [];
    } else {
      mainPayload.departments = departments
        .filter(dept => dept.departmentId && dept.initialBalance)
        .map(dept => ({
          departmentId: dept.departmentId,
          initialBalance: parseInt(dept.initialBalance) || 0
        }));
    }

    // Add usage limit data - NEW STRUCTURE matching API
    if (usageLimitRequired === "Yes" && usageLimits.length > 0) {
      mainPayload.usageLimit = {
        usageLimitRequired: true,
        limits: usageLimits
      };
    }

    console.log("Final Payload:", JSON.stringify(mainPayload, null, 2));

    // Single API call to create corporate with all data
    props.getDataFromAPI(
      `/partner/api/v2/corporate`,
      "POST",
      mainPayload,
      (response) => {
        setLoading(false);
        if (response && response.id) {
          // Store corporate ID and show success modal
          const corporateId = response.id;
          setCorporateId(corporateId);
          setSuccessMessage(`Corporate "${createCorporateData.name.trim()}" created successfully!`);
          setShowSuccessModal(true);
          
          // Also show toast for immediate feedback
          toast.success("Corporate created successfully!");
        } else {
          toast.error("Failed to create corporate: Invalid response from server");
        }
      },
      (err) => {
        setLoading(false);
        console.error("Failed to create corporate:", err);
        
        let errorMessage = "Failed to create corporate";
        if (err.message) {
          errorMessage = err.message;
        } else if (err.response && err.response.data && err.response.data.message) {
          errorMessage = err.response.data.message;
        }
        
        toast.error(errorMessage);
      }
    );
  };

  return (
    <>
      <ToastContainer />
      <CorporateCreateComponent
        activeStep={activeStep}
        handleNext={handleNext}
        handleClose={handleClose}
        createCorporateData={createCorporateData}
        handleChangeCorporate={handleChangeCorporate}
        setCreateCorporateData={setCreateCorporateData}
        departments={departments}
        handleAddDepartment={handleAddDepartment}
        handleRemoveDepartment={handleRemoveDepartment}
        handleDepartmentChange={handleDepartmentChange}
        handleBalanceChange={handleBalanceChange}
        useDefaultDepartment={useDefaultDepartment}
        handleUseDefaultChange={handleUseDefaultChange}
        defaultBalance={defaultBalance}
        handleDefaultBalanceChange={handleDefaultBalanceChange}
        availableDepartments={availableDepartments}
        fetchingDepartments={fetchingDepartments}
        usageLimitRequired={usageLimitRequired}
        handleUsageLimitChange={handleUsageLimitChange}
        onLimitsChange={handleUsageLimitsChange}
        initialLimits={usageLimits}
        loading={loading}
      />

      {/* Success Modal */}
      <Dialog
        open={showSuccessModal}
        onClose={handleSuccessModalClose}
        aria-labelledby="success-dialog-title"
        aria-describedby="success-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            padding: '20px',
            maxWidth: '550px',
            width: '90%'
          }
        }}
      >
        <DialogTitle 
          id="success-dialog-title"
          sx={{ 
            textAlign: 'center',
            paddingTop: '40px',
            paddingBottom: '10px'
          }}
        >
          <CheckCircleIcon 
            sx={{ 
              fontSize: 80, 
              color: '#14AE78',
              marginBottom: '16px'
            }} 
          />
          <div style={{ fontSize: '24px', fontWeight: 600, color: '#333' }}>
            Success!
          </div>
        </DialogTitle>
        <DialogContent>
          <DialogContentText 
            id="success-dialog-description"
            sx={{ 
              textAlign: 'center',
              fontSize: '16px',
              color: '#666',
              lineHeight: '1.6',
              marginBottom: '16px'
            }}
          >
            {successMessage}
          </DialogContentText>
          
          {/* Corporate ID Display */}
          {corporateId && (
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              borderRadius: '10px', 
              padding: '16px', 
              margin: '20px 0',
              border: '1px solid #e8e8e8'
            }}>
              <div style={{ 
                fontSize: '14px', 
                color: '#666', 
                marginBottom: '8px',
                fontWeight: 500
              }}>
                Corporate ID:
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '12px',
                border: '1px solid #ddd'
              }}>
                <div style={{ 
                  fontSize: '16px', 
                  fontFamily: 'monospace',
                  fontWeight: '600',
                  color: '#333',
                  wordBreak: 'break-all'
                }}>
                  {corporateId}
                </div>
                <Button
                  onClick={handleCopyCorporateId}
                  startIcon={<ContentCopyIcon />}
                  size="small"
                  variant="outlined"
                  sx={{
                    marginLeft: '12px',
                    flexShrink: 0,
                    borderColor: '#14AE78',
                    color: '#14AE78',
                    '&:hover': {
                      borderColor: '#0f8c65',
                      backgroundColor: 'rgba(20, 174, 120, 0.05)'
                    }
                  }}
                >
                  {copySuccess ? 'Copied!' : 'Copy'}
                </Button>
              </div>
              <div style={{ 
                fontSize: '13px', 
                color: '#999', 
                marginTop: '8px',
                fontStyle: 'italic'
              }}>
                Save this ID for future reference
              </div>
            </div>
          )}
          
          <DialogContentText 
            sx={{ 
              textAlign: 'center',
              fontSize: '14px',
              color: '#999',
              marginTop: '16px'
            }}
          >
            You will be redirected to the corporate dashboard.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', padding: '24px' }}>
          <Button
            onClick={handleSuccessModalClose}
            variant="contained"
            sx={{
              backgroundColor: '#14AE78',
              color: 'white',
              borderRadius: '10px',
              padding: '12px 48px',
              fontSize: '15px',
              fontWeight: 500,
              textTransform: 'none',
              minWidth: '180px',
              '&:hover': {
                backgroundColor: '#0f8c65',
                boxShadow: '0 4px 12px rgba(20, 174, 120, 0.3)'
              }
            }}
          >
            Go to Corporate Dashboard
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

function mapStateToProps(props) {
  return {
    props,
  };
}

export default connect(mapStateToProps, {
  getDataFromAPI,
})(CorporateCreateContainer);